import _, {identity} from 'lodash';
import {parse, stringify} from 'qs';

import {Dictionary} from '../typescript/typeHelpers';
import {FrontWindow} from './browserHelpers';

export type QueryStringArguments = {[key: string]: any};

/** Schemas allowed by the Front backend (sanitize_htmljs:19). */
const allowedHrefProtocols = ['http:', 'https:', 'ftp:', 'mailto:', 'frontapp:', 'cid:', 'tel:', 'skype:'];

/** Add a query object as query string to a URL path. */
export function addQueryToPath(path: string, query?: QueryStringArguments) {
  if (!query) {
    return path;
  }

  return `${path}${stringifyQueryString(query)}`;
}

/** Add a query object as query string to a path and optional query. */
export function addQueryParamsToPathAndQuery(pathAndQuery: string, query?: QueryStringArguments) {
  // Parse the URL.
  const url = new URL(pathAndQuery, 'https://example.com');

  // Update the query.
  const queryObject = parseQueryString(url.search);
  url.search = stringifyQueryString({
    ...queryObject,
    ...query,
  });

  // Return the new path and query.
  return `${url.pathname}${url.search}`;
}

/** Add a query object as query string to an absolute URL. */
export function addQueryToUrl(url: string, query: QueryStringArguments) {
  const isAbsolute = isAbsoluteUrl(url);

  // If the URL is relative, use a base URL for parsing but return the relative part.
  const absoluteUrl = isAbsolute ? url : new URL(url, 'https://example.com').toString();

  // Parse the URL.
  const parsedUrl = new URL(absoluteUrl);

  // Update the query.
  const queryObject = parseQueryString(parsedUrl.search);
  parsedUrl.search = stringifyQueryString({
    ...queryObject,
    ...query,
  });

  // Return the new URL, keeping it relative if the input was relative.
  return isAbsolute ? parsedUrl.toString() : parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
}

interface ConversationUrlOptions {
  aroundActivityId?: number;
  aroundActivityDate?: number;
  expandMessages?: boolean;
  light?: boolean;
}

/** Build the URL of a conversation resource, without the company path segment. */
export function buildConversationUrl(conversationId: number, options: ConversationUrlOptions = {}) {
  const query = {
    new: 'front',
    around_id: options.aroundActivityId,
    around_date: options.aroundActivityDate,
    light: options.light,
    expanded: options.expandMessages ? 'true' : undefined,
    sort: options.aroundActivityDate === 0 ? 'date' : undefined,
  };
  return addQueryToPath(`/conversations/${conversationId}`, query);
}

interface ConversationContentUrlOptions {
  aroundActivityDate?: number;
  shouldExpandMessages?: boolean;
  limit?: number;
}

interface ConversationContentQueryParams {
  new: string;

  include_tombstoned_comments?: string;

  around_date?: number;
  expanded?: string;
  sort?: string;
  limit?: number;
}

export function buildConversationContentUrl(
  conversationId: number,
  options: ConversationContentUrlOptions = {},
) {
  const query: ConversationContentQueryParams = {
    new: 'front',
    include_tombstoned_comments: 'true',
    around_date: options.aroundActivityDate,
    expanded: options.shouldExpandMessages ? 'true' : undefined,
    sort: options.aroundActivityDate === 0 ? 'date' : undefined,
    limit: options.limit,
  };

  return addQueryToPath(`/conversations/${conversationId}/content`, query);
}

/** Try to parse a string into a URL object. */
export function tryParseUrl(source: string, baseUrl?: string): URL | undefined {
  try {
    // Safari 12 cannot parse new URL(url, undefined).  First check that baseUrl exists and then create the URL accordingly.
    if (baseUrl) {
      return new URL(source, baseUrl);
    }

    return new URL(source);
  } catch (error) {
    return undefined;
  }
}

/** Parse the query string part of a URL. */
export function parseQueryString(qs: string): Dictionary<string | undefined> {
  // Parse.
  const parseOptions = {ignoreQueryPrefix: true};
  const parsed = parse(qs, parseOptions);

  // Normalize keys.
  return _(parsed)
    .mapKeys((_value, key) => key.toLowerCase())
    .mapValues((value) => String(value))
    .value();
}

/** Convert an object to te query string part of a URL. */
export function stringifyQueryString(query: QueryStringArguments) {
  return stringify(query, {
    addQueryPrefix: true,
    encoder: (str, defaultEncoderBase, ...args) => {
      const defaultEncoder = (component: typeof str) =>
        // @ts-expect-error: The qs encoder type is wrong.
        defaultEncoderBase(component, defaultEncoderBase, ...args);

      if (typeof str === 'string') {
        // Slashes look better unencoded, and this is permitted by https://www.rfc-editor.org/rfc/rfc3986.
        return str.split('/').map(defaultEncoder).join('/');
      }

      return defaultEncoder(str);
    },
  });
}

/** Takes what is assumed to be a valid URL and returns something that is safe to be used as an href. */
// See https://snyk.io/blog/secure-javascript-url-validation/
export function sanitizeHref(url: string): string {
  const parsedUrl = tryParseUrl(url);

  if (!parsedUrl || !allowedHrefProtocols.includes(parsedUrl.protocol)) {
    return '';
  }

  return parsedUrl.href;
}

/** Takes a URL entered by a user and returns something that is valid and safe to be used as an href. */
export function sanitizeHrefInput(input: string): string {
  return sanitizeHref(enhanceHrefInput(input));
}

function enhanceHrefInput(input: string): string {
  const trimmedInput = input.trim();
  if (!trimmedInput) {
    return '';
  }

  // Protocol-relative URLs probably support https.
  if (trimmedInput.startsWith('//')) {
    return `https:${trimmedInput}`;
  }

  // Input that is otherwise missing a schema (most common case) is probably intended to be http.
  if (!/^([a-z]+):/.test(trimmedInput)) {
    // International phone number.
    if (/^\+(?:[0-9]( |-)?){6,14}[0-9]$/.test(trimmedInput)) {
      return `tel:${trimmedInput.replace(/[ ()-]/g, '')}`;
    }

    // Email.
    if (/^.+@.+\..+$/.test(trimmedInput)) {
      return `mailto:${trimmedInput}`;
    }

    // HTTP.
    if (/^[\w.:-]+\.[\w:]+(?:\/\S*)?$/.test(trimmedInput)) {
      return `http://${trimmedInput}`;
    }
  }

  return trimmedInput;
}

/** Check whether the provided URL is absolute. */
export function isAbsoluteUrl(url: string) {
  return url.indexOf('://') > 0 || url.startsWith('//');
}

export function removeSchemaFromUrl(url: string) {
  return url.replace(/^(\w+:)?\/\//, '');
}

function mergeQueryParameters(
  baseUrl: string | undefined,
  url: string,
  extraParameters: Dictionary<string | undefined>,
): URL | undefined {
  // Try to parse the provided URL.
  const parsedUrl = tryParseUrl(url, baseUrl);
  if (!parsedUrl) {
    return undefined;
  }

  // Merge the query parameters.
  const queryString = {
    ...parseQueryString(parsedUrl.search),
    ...extraParameters,
  };

  // Rebuild the URL with the merged query parameters.
  parsedUrl.search = stringifyQueryString(queryString);
  return parsedUrl;
}

export function authenticateUrlForWindow(window: FrontWindow, url: string, options: Dictionary<string>) {
  return authenticateUrl(window.location.href, url, options);
}

export function authenticateUrl(baseUrl: string | undefined, url: string, options: Dictionary<string> = {}) {
  const parsedUrl = mergeQueryParameters(baseUrl, url, options);
  return parsedUrl ? parsedUrl.href : url;
}

// https://front.atlassian.net/browse/PB-49435
export function checkIsMimecastProtectUrl(url: URL) {
  // Mimecast has changed its mind over time, so perform a more lenient check.
  const conditions: ReadonlyArray<boolean> = [
    url.host.includes('mimecast'),
    url.host.includes('protect'),
    url.searchParams.has('domain'),
  ];
  return conditions.filter(identity).length >= 2;
}

/*
 * Visitor token.
 */

const visitorTokenRegexp = /^(?:\/v2)?\/visitors\/([^/]+)$/i;

/** Find the visitors authentication token in a URL. */
export function tryParseVisitorToken(path: string) {
  const match = path.match(visitorTokenRegexp);
  if (!match || !match[1]) {
    return undefined;
  }

  return match[1];
}

/*
 * Topics.
 */

const urlProtocolRegexp = /^(?:http(|s)|ftp|scp|ssh):\/\//;

export function removeProtocolFromUrl(url: string) {
  return url.replace(urlProtocolRegexp, '');
}

/** Open the URL for a topic in a browser. */
export function sanitizeTopicLinkUrl(url: string) {
  // A topic can be any string.
  // If the protocol is missing we add it.
  if (!/^http(s?):\/\//.test(url)) {
    return `https://${url}`;
  }

  return url;
}
